import { useEffect, useState } from "react";
import { api, fmt } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const TAG_OPTIONS = ["blankets", "towels", "fragrance", "collections"];
const empty = {
  name: "", subcategory: "", tags: [], price: "",
  image: "", image2: "", material: "", tagline: "", features: "", sizes: "", colors: "", featured: false,
};

export default function Admin() {
  const [authed, setAuthed] = useState(() => Boolean(sessionStorage.getItem("rinova_admin_token")));
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ products: 0, orders: 0, revenue: 0 });
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/orders").then((r) => setOrders(r.data));
    api.get("/stats").then((r) => setStats(r.data));
  };
  useEffect(() => { if (authed) load(); }, [authed]);

  if (!authed) return <AdminGate onAuth={() => setAuthed(true)} />;

  const openCreate = () => { setForm(empty); setEditingId(null); setShowForm(true); };
  const openEdit = (p) => {
    setForm({
      name: p.name, subcategory: p.subcategory || "", tags: p.tags || [], price: String(p.price),
      image: p.image, image2: p.image2 || "", material: p.material, tagline: p.tagline,
      features: (p.features || []).join("\n"),
      sizes: (p.sizes || []).map((s) => `${s.label}|${s.price}`).join("\n"),
      colors: (p.colors || []).join(", "), featured: !!p.featured,
    });
    setEditingId(p.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const doDelete = async (p) => {
    await api.delete(`/products/${p.id}`);
    toast("Product deleted");
    setConfirmDelete(null);
    load();
  };

  const toggleTag = (t) =>
    setForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name, subcategory: form.subcategory, tags: form.tags,
        price: parseFloat(form.price) || 0, image: form.image, image2: form.image2 || null,
        material: form.material, tagline: form.tagline,
        features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
        sizes: form.sizes.split("\n").map((s) => {
          const [label, price] = s.split("|");
          return label ? { label: label.trim(), price: parseFloat(price) || parseFloat(form.price) || 0 } : null;
        }).filter(Boolean),
        colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
        featured: form.featured,
        collection: form.tags.includes("collections"),
      };
      if (editingId) { await api.put(`/products/${editingId}`, payload); toast("Product updated"); }
      else { await api.post("/products", payload); toast("Product created"); }
      setForm(empty); setEditingId(null); setShowForm(false); load();
    } catch { toast("Failed to save product"); }
  };

  const logout = () => { sessionStorage.removeItem("rinova_admin_token"); setAuthed(false); };
  const inputCls = "border border-border px-4 py-3 bg-transparent outline-none focus:border-black text-sm";

  return (
    <div data-testid="admin-page" className="pt-16">
      <div className="px-6 md:px-10 lg:px-20 pt-16 pb-28">
        <Reveal>
          <div className="flex items-start justify-between">
            <div>
              <p className="util-link text-[11px] caption mb-3">Dashboard</p>
              <h1 className="font-display text-5xl sm:text-6xl uppercase">rinova admin</h1>
            </div>
            <button data-testid="admin-logout" onClick={logout} className="util-link text-[11px] link-hover">Log out</button>
          </div>
        </Reveal>

        <div className="grid grid-cols-3 gap-px bg-border mt-12">
          {[{ l: "Products", v: stats.products }, { l: "Orders", v: stats.orders }, { l: "Revenue", v: fmt(stats.revenue) }].map((s) => (
            <div key={s.l} className="bg-white p-7">
              <div className="font-display text-4xl">{s.v}</div>
              <div className="util-link text-[10px] caption mt-2">{s.l}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-6 items-center mt-12 mb-8 border-b border-border pb-4">
          {["products", "orders"].map((t) => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
              className={`product-title text-[13px] link-hover pb-1 ${tab === t ? "border-b border-black" : "text-graphite"}`}>{t}</button>
          ))}
          {tab === "products" && (
            <button data-testid="admin-add-toggle" onClick={() => (showForm && !editingId ? setShowForm(false) : openCreate())}
              className="ml-auto bg-black text-white px-6 py-2.5 product-title text-[12px] link-hover">New Product</button>
          )}
        </div>

        {tab === "products" && showForm && (
          <form onSubmit={save} data-testid="admin-product-form" className="bg-card p-8 mb-10 grid md:grid-cols-2 gap-5">
            <p data-testid="form-mode" className="md:col-span-2 product-title text-[13px]">{editingId ? "Edit Product" : "New Product"}</p>
            <input required placeholder="Name" data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            <input required placeholder="Price" type="number" data-testid="form-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} />
            <div className="md:col-span-2">
              <p className="util-link text-[11px] caption mb-3">Categories (multi-select)</p>
              <div className="flex flex-wrap gap-3">
                {TAG_OPTIONS.map((t) => (
                  <button type="button" key={t} data-testid={`form-tag-${t}`} onClick={() => toggleTag(t)}
                    className={`px-4 py-2 text-[12px] product-title border border-black link-hover ${form.tags.includes(t) ? "bg-black text-white" : "bg-transparent text-black"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <input placeholder="Label (e.g. Super Soft Blankets)" data-testid="form-subcategory" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className={inputCls} />
            <input placeholder="Material" data-testid="form-material" value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} className={inputCls} />
            <input placeholder="Image URL" data-testid="form-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={`${inputCls} md:col-span-2`} />
            <input placeholder="Hover Image URL (optional)" data-testid="form-image2" value={form.image2} onChange={(e) => setForm({ ...form, image2: e.target.value })} className={`${inputCls} md:col-span-2`} />
            <input placeholder="Tagline" data-testid="form-tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className={`${inputCls} md:col-span-2`} />
            <textarea placeholder="Features (one per line)" rows={3} value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} className={inputCls} />
            <textarea placeholder="Sizes (Label|Price per line)" rows={3} value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} className={inputCls} />
            <input placeholder="Colors (comma separated)" value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} className={`${inputCls} md:col-span-2`} />
            <label className="flex items-center gap-2 text-sm md:col-span-2"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured on homepage</label>
            <div className="md:col-span-2 flex gap-3">
              <button data-testid="form-submit" className="bg-black text-white py-3 px-8 product-title text-[13px] link-hover">{editingId ? "Update Product" : "Create Product"}</button>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); setForm(empty); }} className="border border-black py-3 px-8 product-title text-[13px] link-hover">Cancel</button>
            </div>
          </form>
        )}

        {tab === "products" && (
          <div data-testid="admin-products" className="border border-border">
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.slug}`} className="flex items-center gap-4 p-4 border-b border-border last:border-0">
                <img src={p.image} alt={p.name} className="w-14 h-16 object-cover bg-card shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="product-title text-[12px] truncate">{p.name}</p>
                  <p className="text-xs caption">{(p.tags && p.tags.length ? p.tags.join(" · ") : "uncategorized")}</p>
                </div>
                <span className="text-sm shrink-0">{fmt(p.price)}</span>
                <button data-testid={`admin-edit-${p.slug}`} onClick={() => openEdit(p)}
                  className="shrink-0 bg-black text-white border border-black px-4 py-1.5 text-[11px] product-title hover:bg-transparent hover:text-black transition-colors">
                  Edit
                </button>
                <button data-testid={`admin-delete-${p.slug}`} onClick={() => setConfirmDelete(p)}
                  className="shrink-0 border border-red-600 text-red-600 bg-transparent px-4 py-1.5 text-[11px] product-title hover:bg-red-600 hover:text-white transition-colors">
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "orders" && (
          <div data-testid="admin-orders" className="space-y-4">
            {orders.length === 0 && <p className="caption py-10 text-center text-sm">No orders yet.</p>}
            {orders.map((o) => (
              <div key={o.id} className="border border-border p-6">
                <div className="flex justify-between flex-wrap gap-2">
                  <div>
                    <p className="product-title text-[12px]">#{o.id.slice(0, 8).toUpperCase()} · {o.customer_name}</p>
                    <p className="text-xs caption">{o.email} · {new Date(o.created_at).toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <span className="util-link text-[10px] border border-black px-3 py-1">{o.payment_status}</span>
                    <p className="text-lg mt-1">{fmt(o.subtotal)}</p>
                  </div>
                </div>
                <p className="text-sm caption mt-3">{o.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* DELETE CONFIRMATION */}
      <AnimatePresence>
        {confirmDelete && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setConfirmDelete(null)} className="fixed inset-0 z-[80] bg-black/40" />
            <motion.div data-testid="delete-confirm"
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
              className="fixed z-[90] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-8 w-[92%] max-w-md">
              <h3 className="font-display text-2xl uppercase mb-4">Delete product</h3>
              <p className="text-sm caption mb-8">
                Are you sure you want to delete <span className="text-black">{confirmDelete.name}</span>? This action cannot be undone and will remove it from the home page.
              </p>
              <div className="flex gap-3 justify-end">
                <button data-testid="delete-cancel" onClick={() => setConfirmDelete(null)}
                  className="border border-black px-6 py-2.5 product-title text-[12px] link-hover">Cancel</button>
                <button data-testid="delete-confirm-btn" onClick={() => doDelete(confirmDelete)}
                  className="bg-red-600 text-white border border-red-600 px-6 py-2.5 product-title text-[12px] hover:bg-transparent hover:text-red-600 transition-colors">Delete</button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

function AdminGate({ onAuth }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/admin/login", { password: pw });
      sessionStorage.setItem("rinova_admin_token", r.data.token);
      onAuth();
    } catch {
      setErr(true); setPw("");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-gate" className="fixed inset-0 z-[80] bg-white flex flex-col items-center justify-center px-6">
      <p className="util-link text-[11px] caption mb-3">rinova admin</p>
      <h1 className="font-display text-4xl uppercase mb-10">Enter Password</h1>
      <form onSubmit={submit} className="w-full max-w-xs">
        <input data-testid="admin-password-input" type="password" autoFocus value={pw}
          onChange={(e) => { setPw(e.target.value); setErr(false); }}
          className="w-full bg-transparent border-b border-black py-3 text-center outline-none text-sm tracking-widest" placeholder="••••••••" />
        {err && <p data-testid="admin-error" className="text-center text-[12px] mt-4">Incorrect password</p>}
        <button data-testid="admin-submit" disabled={loading}
          className="w-full bg-black text-white py-4 mt-8 product-title text-[13px] link-hover disabled:opacity-50">
          {loading ? "Checking…" : "Unlock"}
        </button>
      </form>
    </div>
  );
}
